import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useFeatureAccess } from '../hooks/useFeatureAccess'
import { exportExaminationToPDF, exportExaminationComparisonToPDF } from '../services/examinationExport'
import Card from './Card'
import Button from './Button'
import Select from './Select'
import { GitCompare, Download, AlertCircle, FileText } from 'lucide-react'
import { formatDate } from '../utils/helpers'
import FeatureGate from './FeatureGate'
import UpgradePrompt from './UpgradePrompt'

const ExaminationComparison = ({ clientId, client }) => {
  const organizationId = useOrganizationId()
  const { hasAccess, loading: accessLoading } = useFeatureAccess('examination_comparison')
  const [examinations, setExaminations] = useState([])
  const [selectedExam1, setSelectedExam1] = useState(null)
  const [selectedExam2, setSelectedExam2] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    if (organizationId && clientId) {
      fetchExaminations()
    }
  }, [organizationId, clientId])

  const fetchExaminations = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('examinations')
        .select('*, medical_history:medical_history_id (*)')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setExaminations(data || [])
      if (data && data.length > 0) {
        setSelectedExam1(data[0].id)
        if (data.length > 1) {
          setSelectedExam2(data[1].id)
        }
      }
    } catch (error) {
      console.error('Error fetching examinations:', error)
    } finally {
      setLoading(false)
    }
  }

  const getExam1 = () => examinations.find((e) => e.id === selectedExam1)
  const getExam2 = () => examinations.find((e) => e.id === selectedExam2)

  const compareValues = (val1, val2, label) => {
    const v1 = val1 || 'N/A'
    const v2 = val2 || 'N/A'
    const changed = v1 !== v2
    return { v1, v2, changed, label }
  }

  const handleExportSingle = async (examId) => {
    try {
      setExporting(true)
      const exam = examinations.find((e) => e.id === examId)
      if (!exam) return

      const medicalHistory = exam.medical_history || null
      await exportExaminationToPDF(exam, client, medicalHistory)
    } catch (error) {
      console.error('Error exporting examination:', error)
      alert('Failed to export examination. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const handleExportComparison = async () => {
    if (!selectedExam1 || !selectedExam2) return

    try {
      setExporting(true)
      const exam1 = getExam1()
      const exam2 = getExam2()
      if (!exam1 || !exam2) return

      const medicalHistory = exam1.medical_history || exam2.medical_history || null
      await exportExaminationComparisonToPDF(exam1, exam2, client, medicalHistory)
    } catch (error) {
      console.error('Error exporting comparison:', error)
      alert('Failed to export comparison. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  if (accessLoading) {
    return (
      <Card>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    )
  }

  if (!hasAccess) {
    return (
      <Card>
        <UpgradePrompt
          featureCode="examination_comparison"
          featureName="Examination Comparison & PDF Export"
          description="Compare examination records side-by-side and export to PDF. This premium feature is available on paid subscription tiers."
        />
      </Card>
    )
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8 text-gray-500">Loading examinations...</div>
      </Card>
    )
  }

  if (examinations.length < 2) {
    return (
      <Card>
        <div className="text-center py-8">
          <GitCompare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {examinations.length === 0
              ? 'No examinations found. Create at least two examinations to compare.'
              : 'Only one examination found. Create another examination to compare.'}
          </p>
        </div>
      </Card>
    )
  }

  const exam1 = getExam1()
  const exam2 = getExam2()

  if (!exam1 || !exam2) {
    return (
      <Card>
        <div className="text-center py-8 text-gray-500">Please select two examinations to compare.</div>
      </Card>
    )
  }

  const vitalComparisons = [
    compareValues(exam1.vital_signs?.bloodPressure, exam2.vital_signs?.bloodPressure, 'Blood Pressure'),
    compareValues(exam1.vital_signs?.heartRate, exam2.vital_signs?.heartRate, 'Heart Rate'),
    compareValues(exam1.vital_signs?.respiratoryRate, exam2.vital_signs?.respiratoryRate, 'Respiratory Rate'),
    compareValues(exam1.vital_signs?.temperature, exam2.vital_signs?.temperature, 'Temperature'),
    compareValues(exam1.vital_signs?.oxygenSaturation, exam2.vital_signs?.oxygenSaturation, 'SpO₂'),
    compareValues(exam1.vital_signs?.weight, exam2.vital_signs?.weight, 'Weight'),
    compareValues(exam1.vital_signs?.height, exam2.vital_signs?.height, 'Height'),
    compareValues(exam1.vital_signs?.bmi, exam2.vital_signs?.bmi, 'BMI'),
  ]

  return (
    <div className="space-y-4">
      {/* Selection Controls */}
      <Card variant="soft" className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">First Examination</label>
            <Select
              value={selectedExam1}
              onChange={(e) => setSelectedExam1(e.target.value)}
              options={examinations.map((exam) => ({
                value: exam.id,
                label: `${formatDate(exam.created_at)}${exam.vital_signs?.bloodPressure ? ` - BP: ${exam.vital_signs.bloodPressure}` : ''}`,
              }))}
            />
            {exam1 && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => handleExportSingle(exam1.id)}
                disabled={exporting}
              >
                <Download size={16} className="mr-1" />
                Export PDF
              </Button>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Second Examination</label>
            <Select
              value={selectedExam2}
              onChange={(e) => setSelectedExam2(e.target.value)}
              options={examinations
                .filter((exam) => exam.id !== selectedExam1)
                .map((exam) => ({
                  value: exam.id,
                  label: `${formatDate(exam.created_at)}${exam.vital_signs?.bloodPressure ? ` - BP: ${exam.vital_signs.bloodPressure}` : ''}`,
                }))}
            />
            {exam2 && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => handleExportSingle(exam2.id)}
                disabled={exporting}
              >
                <Download size={16} className="mr-1" />
                Export PDF
              </Button>
            )}
          </div>
        </div>
        <Button
          onClick={handleExportComparison}
          disabled={exporting || !selectedExam1 || !selectedExam2}
          className="w-full"
        >
          <FileText size={18} className="mr-2" />
          {exporting ? 'Exporting...' : 'Export Comparison PDF'}
        </Button>
      </Card>

      {/* Comparison View */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <GitCompare size={24} />
            Comparison View
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Column 1 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">
              {formatDate(exam1.created_at)}
            </h4>

            {/* Vital Signs */}
            <div className="space-y-3 mb-6">
              <h5 className="text-sm font-semibold text-gray-700">Vital Signs</h5>
              {vitalComparisons.map((comp, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">{comp.label}</div>
                  <div className="text-sm font-medium text-gray-900">{comp.v1}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Column 2 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-500 uppercase mb-4">
              {formatDate(exam2.created_at)}
            </h4>

            {/* Vital Signs */}
            <div className="space-y-3 mb-6">
              <h5 className="text-sm font-semibold text-gray-700">Vital Signs</h5>
              {vitalComparisons.map((comp, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg ${
                    comp.changed ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-gray-50'
                  }`}
                >
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    {comp.label}
                    {comp.changed && (
                      <AlertCircle size={12} className="text-yellow-600" title="Changed" />
                    )}
                  </div>
                  <div className={`text-sm font-medium ${comp.changed ? 'text-yellow-900' : 'text-gray-900'}`}>
                    {comp.v2}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary of Changes */}
        {vitalComparisons.some((c) => c.changed) && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h5 className="text-sm font-semibold text-yellow-900 mb-2">Summary of Changes</h5>
            <ul className="text-sm text-yellow-800 space-y-1">
              {vitalComparisons
                .filter((c) => c.changed)
                .map((c, idx) => (
                  <li key={idx}>
                    • {c.label}: {c.v1} → {c.v2}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  )
}

export default ExaminationComparison

